import React, { useEffect, useState } from "react";
import { ArrowLeft, CheckCircle2, ClipboardList, Heart, RefreshCcw, ShieldCheck } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import {
  fetchQuitPlan,
  generateQuitPlanFromCheckIn,
  updateQuitPlanEdits,
} from "../services/quitPlanApi";
import "./QuitPlanPage.css";

const emptyEdits = {
  support_person: "",
  motivation_note: "",
  personal_notes: "",
};

function getEditsFromPlan(quitPlan) {
  return quitPlan?.plan?.personalEdits || emptyEdits;
}

const QuitPlanPage = () => {
  const navigate = useNavigate();
  const [quitPlan, setQuitPlan] = useState(null);
  const [edits, setEdits] = useState(emptyEdits);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [editing, setEditing] = useState(false);
  const [error, setError] = useState("");
  const [needsCheckIn, setNeedsCheckIn] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const loadPlan = async () => {
      try {
        const data = await fetchQuitPlan();

        if (cancelled) return;

        if (data.quit_plan) {
          setQuitPlan(data.quit_plan);
          setEdits(getEditsFromPlan(data.quit_plan));
          return;
        }

        const generated = await generateQuitPlanFromCheckIn();

        if (!cancelled) {
          setQuitPlan(generated.quit_plan);
          setEdits(getEditsFromPlan(generated.quit_plan));
        }
      } catch (err) {
        if (!cancelled) {
          if (err.message?.toLowerCase().includes("check-in")) {
            setNeedsCheckIn(true);
          } else {
            setError(err.message || "Failed to load quit plan.");
          }
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPlan();

    return () => {
      cancelled = true;
    };
  }, []);

  const handleEditChange = (event) => {
    const { name, value } = event.target;
    setEdits((current) => ({ ...current, [name]: value }));
  };

  const handleSaveEdits = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError("");

    try {
      const data = await updateQuitPlanEdits(edits);
      setQuitPlan(data.quit_plan);
      setEdits(getEditsFromPlan(data.quit_plan));
      setEditing(false);
    } catch (err) {
      setError(err.message || "Failed to save quit plan updates.");
    } finally {
      setSaving(false);
    }
  };

  const handleRegenerate = async () => {
    setGenerating(true);
    setError("");

    try {
      const data = await generateQuitPlanFromCheckIn();
      setQuitPlan(data.quit_plan);
      setEdits(getEditsFromPlan(data.quit_plan));
      setNeedsCheckIn(false);
      setEditing(false);
    } catch (err) {
      if (err.message?.toLowerCase().includes("check-in")) {
        setNeedsCheckIn(true);
      } else {
        setError(err.message || "Failed to regenerate quit plan.");
      }
    } finally {
      setGenerating(false);
    }
  };

  if (loading) {
    return (
      <main className="quit-plan-page">
        <div className="quit-plan-page__container">
          <p className="quit-plan-page__status">Building quit plan...</p>
        </div>
      </main>
    );
  }

  const plan = quitPlan?.plan || null;

  return (
    <main className="quit-plan-page">
      <div className="quit-plan-page__container">
        <button
          type="button"
          className="quit-plan-page__back"
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeft aria-hidden="true" />
          Back to Dashboard
        </button>

        <header className="quit-plan-page__header">
          <div className="quit-plan-page__icon" aria-hidden="true">
            <ClipboardList />
          </div>
          <div>
            <p className="quit-plan-page__eyebrow">Generated from check-in</p>
            <h1>Your Quit Plan</h1>
            <p>
              QuitPact uses your latest check-in result to prepare practical
              support for cravings, triggers, and high risk moments.
            </p>
          </div>
        </header>

        {error && (
          <p className="quit-plan-page__error" role="alert">
            {error}
          </p>
        )}

        {needsCheckIn && (
          <section className="quit-plan-panel quit-plan-panel--empty">
            <h2>Complete a check-in first</h2>
            <p>
              Your quit plan is generated from your latest check-in, so it needs
              one saved result before it can personalise the strategies.
            </p>
            <Link to="/check-in" className="quit-plan-page__primary-link">
              Start check in
            </Link>
          </section>
        )}

        {plan && (
          <>
            <section className="quit-plan-panel quit-plan-panel--hero">
              <div>
                <p className="quit-plan-panel__label">
                  {plan.summary?.riskLevel} risk profile
                </p>
                <h2>{plan.summary?.firstStep}</h2>
                <p className="quit-plan-panel__meta">
                  Total score: {plan.summary?.totalScore} / 100
                </p>
              </div>
              <div className="quit-plan-panel__actions">
                <button
                  type="button"
                  className="quit-plan-page__secondary-button"
                  onClick={handleRegenerate}
                  disabled={generating}
                >
                  <RefreshCcw aria-hidden="true" />
                  {generating ? "Regenerating..." : "Use latest check-in"}
                </button>
                <button
                  type="button"
                  className="quit-plan-page__secondary-button"
                  onClick={() => setEditing((current) => !current)}
                >
                  {editing ? "Close edits" : "Personalise"}
                </button>
              </div>
            </section>

            {editing && (
              <form className="quit-plan-panel quit-plan-edit" onSubmit={handleSaveEdits}>
                <h2>Personal touches</h2>
                <div className="quit-plan-form__grid">
                  <label>
                    Support person
                    <input
                      type="text"
                      name="support_person"
                      value={edits.support_person}
                      onChange={handleEditChange}
                      placeholder="Example: Sam"
                    />
                  </label>

                  <label>
                    Motivation reminder
                    <input
                      type="text"
                      name="motivation_note"
                      value={edits.motivation_note}
                      onChange={handleEditChange}
                      placeholder="Example: I want to breathe easier"
                    />
                  </label>
                </div>

                <label>
                  Personal notes
                  <textarea
                    name="personal_notes"
                    value={edits.personal_notes}
                    onChange={handleEditChange}
                    rows="4"
                    placeholder="Add anything you want to remember during a high risk moment"
                  />
                </label>

                <div className="quit-plan-form__actions">
                  <button
                    type="submit"
                    className="quit-plan-page__primary-button"
                    disabled={saving}
                  >
                    {saving ? "Saving..." : "Save updates"}
                  </button>
                </div>
              </form>
            )}

            <section className="quit-plan-grid">
              <article className="quit-plan-panel">
                <div className="quit-plan-panel__heading">
                  <ShieldCheck aria-hidden="true" />
                  <h2>During a craving</h2>
                </div>
                <ol className="quit-plan-steps">
                  {(plan.cravingPlan || []).map((step) => (
                    <li key={step}>{step}</li>
                  ))}
                </ol>
              </article>

              <article className="quit-plan-panel">
                <div className="quit-plan-panel__heading">
                  <Heart aria-hidden="true" />
                  <h2>Motivation</h2>
                </div>
                <ul className="quit-plan-list">
                  {(plan.motivationalReminders || []).map((reminder) => (
                    <li key={reminder}>{reminder}</li>
                  ))}
                </ul>
                {plan.personalEdits?.personal_notes && (
                  <p className="quit-plan-note">{plan.personalEdits.personal_notes}</p>
                )}
              </article>
            </section>

            <section className="quit-plan-panel">
              <div className="quit-plan-panel__heading">
                <CheckCircle2 aria-hidden="true" />
                <h2>What to prepare for</h2>
              </div>
              <div className="quit-plan-trigger-list">
                {(plan.triggerPlan || []).map((item) => (
                  <article key={`${item.trigger}-${item.response}`} className="quit-plan-trigger">
                    <h3>{item.trigger}</h3>
                    <p>{item.response}</p>
                    {item.rationale && <span>{item.rationale}</span>}
                  </article>
                ))}
              </div>
            </section>

            <section className="quit-plan-panel">
              <h2>Daily actions</h2>
              <ul className="quit-plan-list">
                {(plan.dailyActions || []).map((action) => (
                  <li key={action}>{action}</li>
                ))}
              </ul>
            </section>
          </>
        )}

        <Link to="/dashboard" className="quit-plan-page__mobile-back">
          Back to Dashboard
        </Link>
      </div>
    </main>
  );
};

export default QuitPlanPage;
